import type { JobCategoryRef } from '@seek/job-category';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Column,
  Columns,
  Secondary,
  Stack,
  Text,
} from 'braid-design-system';
import React, {
  type Ref,
  useRef,
  forwardRef,
  type RefObject,
  useContext,
} from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import { SECTION_ROLE_INFO } from 'src/app/constants/pageSectionName';
import { useWorkTypeTaxonomy } from 'src/app/taxonomies/useWorkTypeTaxonomy';
import { JobCategory } from 'src/app/views/components/JobCategory';
import { JobLocation } from 'src/app/views/components/JobLocation';
import { JobTitle } from 'src/app/views/components/JobTitle';
import { WorkType } from 'src/app/views/components/WorkType';
import { EditFlowDataContext } from 'src/app/views/context/cajaFlowData';
import { EditablePageSection } from 'src/app/views/sections/EditablePageSection';
import { ContentLoader } from 'src/app/views/shared/ContentLoader';
import type AppState from 'src/types/appState';
import type { JobLocationRef, JobTitleRef } from 'src/types/formFields';
import type { SectionProps, SectionRef } from 'src/types/sections';

import translations from './.vocab';

const Read = () => {
  const { categoryText, jobTitle, jobLocationText, subCategoryText, workType } =
    useSelector(
      (state: AppState) => ({
        categoryText: state.session.categoryText,
        jobTitle: state.formData.jobTitle,
        jobLocationText: state.session.jobLocationText,
        subCategoryText: state.session.subCategoryText,
        workType: state.formData.workType,
      }),
      shallowEqual,
    );

  const { t } = useTranslations(translations);

  const { loading } = useContext(EditFlowDataContext);

  const { getWorkTypeText } = useWorkTypeTaxonomy();

  return (
    <Stack space="large">
      <Columns space="small">
        <Column width="1/4">
          <Text weight="strong">
            {t('role-information-section-read-job-title-label')}
          </Text>
        </Column>
        <Column>
          <ContentLoader loading={loading} lines={['50%']} offset>
            <Text
              data={{ testid: 'role-information-section-read-job-title-value' }}
            >
              {jobTitle}
            </Text>
          </ContentLoader>
        </Column>
      </Columns>
      <Columns space="xsmall">
        <Column width="1/4">
          <Text weight="strong">
            {t('role-information-section-read-location-label')}
          </Text>
        </Column>
        <Column>
          <ContentLoader loading={loading} lines={['50%']} offset>
            <Text>{jobLocationText}</Text>
          </ContentLoader>
        </Column>
      </Columns>
      <Columns space="xsmall">
        <Column width="1/4">
          <Text weight="strong">
            {t('role-information-section-read-category-label')}
          </Text>
        </Column>
        <Column>
          <ContentLoader loading={loading} lines={['75%']} offset>
            <Text>
              {categoryText} <Secondary> • </Secondary> {subCategoryText}
            </Text>
            {/* Using our JobCategory component here, as a low-risk method to return correctly translated categories
                TODO: remove JOB_CATEGORIES constant, and references to legacy Adcentre (sub)category IDs */}
            <Box hidden>
              <JobCategory />
            </Box>
          </ContentLoader>
        </Column>
      </Columns>
      <Columns space="xsmall">
        <Column width="1/4">
          <Text weight="strong">
            {t('role-information-section-read-work-type-label')}
          </Text>
        </Column>
        <Column>
          <ContentLoader loading={loading} lines={['15%']} offset>
            <Text>{getWorkTypeText(workType)}</Text>
          </ContentLoader>
        </Column>
      </Columns>
    </Stack>
  );
};

interface EditProps {
  refs: {
    jobTitleRef: RefObject<JobTitleRef>;
    jobLocationRef: RefObject<JobLocationRef>;
    jobCategoryRef: RefObject<JobCategoryRef>;
  };
}

const Edit = (props: EditProps) => (
  <Stack space="medium">
    <JobTitle ref={props.refs.jobTitleRef} />
    <JobLocation
      limitSuggestionsToExistingCountry={true}
      ref={props.refs.jobLocationRef}
    />
    <JobCategory ref={props.refs.jobCategoryRef} />
    <WorkType />
  </Stack>
);

export const RoleInformationSection = forwardRef(
  (_props: SectionProps, ref: Ref<SectionRef>) => {
    const jobTitleRef = useRef<JobTitleRef>(null);
    const jobLocationRef = useRef<JobLocationRef>(null);
    const jobCategoryRef = useRef<any>(null);

    const { t } = useTranslations(translations);

    return (
      <EditablePageSection
        title={t('role-information-section-title')}
        name={SECTION_ROLE_INFO}
        ref={ref}
        fields={[jobTitleRef, jobLocationRef, jobCategoryRef]}
        read={<Read />}
        edit={
          <Edit
            refs={{
              jobTitleRef,
              jobLocationRef,
              jobCategoryRef,
            }}
          />
        }
      />
    );
  },
);
